<template>
	<div class="step d-flex justify-content-center" v-if="loadingPaymentMethods">
		<SpinnerOne/>
	</div>

	<div class="step" v-else>
		<InvoiceSummary v-if="merchant().ShowPaymentSummary" />

		<CardDetails v-model="collected_info" v-if="card_methods.length > 0" />

		<SecondaryDetails v-if="non_card_methods.length > 0" />

		<FinancingDetails v-if="show_financing_options" />

		<ErrorMessage :message="error_message" v-if="error_message.length > 0" />
		<WarningMessage :message="warning_message" v-if="warning_message.length > 0"/>

		<div class="button-box">
			<div class="row">
				<div class="col-xs-12 col-lg-4 order-2 order-lg-1">
					<a class="start-over" @click="clear()">{{ getText("StartOver") }}</a>
				</div>
				<div class="col-xs-12 col-lg-4 order-3 order-lg-2">
					<div class="contact-text">
						<b>{{ getText("NeedHelp") }}</b>
						<a
							class="support-link"
							:href="'mailto:' + merchant().SupportEmail"
							>{{ merchant().SupportEmail }}</a
						>
					</div>
				</div>
				<div class="col-xs-12 col-lg-4 order-1 order-lg-3">
					<input
						class="submit-button"
						readonly
						:value="getText('NextStep')"
						@click="validate()"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters } from "vuex";
import store from "@/store";
import { CheckoutService } from "@/common/api.service";

// Language / Localization
import LanguageService from "@/common/language.service";

import {
	SET_PAYMENT_DETAILS,
	SET_PAYMENT_METHOD,
	SET_STASH_DATA,
	CLEAR_STASH,
} from "@/store/mutations.type";

import { GET_PAYMENT_METHODS } from "@/store/actions.type";

// Import Major components
import InvoiceSummary from "@/components/steps/invoice_step/InvoiceSummary";
import CardDetails from "@/components/steps/payment_step/payment_collection/CardDetails";
import SecondaryDetails from "@/components/steps/payment_step/payment_collection/SecondaryDetails";
import FinancingDetails from "@/components/steps/payment_step/payment_collection/FinancingDetails";

/* Smaller elements */
import WarningMessage from "@/components/messages/WarningMessage";
import ErrorMessage from "@/components/messages/ErrorMessage";
import SpinnerOne from "@/components/helpers/SpinnerOne";

export default {
	name: "PaymentStep",
	components: {
		InvoiceSummary,
		CardDetails,
		SecondaryDetails,
		FinancingDetails,

		WarningMessage,
		ErrorMessage,
		SpinnerOne,
	},
	beforeMount() {
		// load payment methods
		const payload = {
			MerchantId: this.merchant().MerchantId,
			Currency: this.invoice().payment_currency,
			Country: this.billing.Country,
		};

		store.dispatch(GET_PAYMENT_METHODS, payload)
			.then(() => {
				this.loadingPaymentMethods = false;
			})
			.catch((error) => {
				console.error(error);
				this.$router.push({ name: 'Error', params: { error: error }});
			});

		// PRESET method to card
		var cards = this.paymentMethods.filter((x) => x.Class == "Card");
		if (cards.length > 0) {
			var card = {};
			//Try to preset VISA first
			if (cards.filter((x) => x.Id == "VISA").length > 0) {
				card = cards.filter((x) => x.Id == "VISA")[0];
			} else {
				// Otherwise, just pick first one
				card = cards[0];
			}
			// Set default payment method to selected card
			Promise.all([store.commit(SET_PAYMENT_METHOD, card)]);
		}
	},
	data() {
		// Our primary data objects (used to store user data and progress)
		return {
			loadingPaymentMethods: true,
			error_message: "",
			warning_message: "",
			collected_information: this.payment_details(),
			chosen_payment_method: this.payment_method()
		}
	},
	watch: {
		// If our computed payment_request obejct has changes, we want to
		// pre-cache our available payment methods.
		selected_payment_method(val) {
			// Otherwise, grab n' store our new payment methods behind the scenes
			Promise.all([store.commit(SET_PAYMENT_METHOD, val)]);
		},
		paymentMethods(paymentMethods) {
			if(paymentMethods && paymentMethods.length > 0) return;
			this.error_message =this.getError('NoPaymentMethods')
		},
	},
	computed: {
		...mapGetters(["paymentMethods", "billing"]),
		collected_info: {
			get(){
				return this.collected_information;
			},
			set(data){
				this.collected_information = data;
			}
		},
		cleaned_info() {
			var info = this.collected_information;
			info.Number = info.Number.replace(/\s/g, '');
			return info;
		},
		selected_payment_method: {
			get(){
				return this.chosen_payment_method;
			},
			set(data){
				this.chosen_payment_method = data;
			}
		},
		show_financing_options(){
			var options_exist = false;
			var current_method_financing = false;

			// Validate that we have financing options
			if(this.financing_options().Instalments !== undefined){
				if(this.financing_options().Instalments.length > 0) options_exist = true;
			}
			// Validate that selected payment method has financing enabled
			if(this.payment_method().Financing !== undefined){
				if(this.payment_method().Financing) current_method_financing = true;
			}
			return (options_exist && current_method_financing);
		},
		non_card_methods(){
			return this.paymentMethods.filter(x => (x.Class != "Card"));
		},
		card_methods(){
			// Filter away all non card payment methods
			return this.paymentMethods.filter(x => (x.Class == "Card"));
		}
	},
	methods: {
		getText(key){
			return LanguageService.getText(key);
		},
		getError(key){
			return LanguageService.getError(key);
		},
		clear: function(){
			this.$emit('start_over', true);
		},
		validate: function(){
			this.error_message = '';
			this.warning_message = '';

			if(this.payment_method() == {} || this.payment_method().Id === undefined || this.payment_method().Id == ""){
				if(this.payment_method().Class == "Card"){
					this.error_message = this.getError('MissingCreditCardType');
				} else {
					this.error_message = this.getError('MissingPaymentMethod');
				}
				return;
			}

			// Validate payment method by type
			switch(this.payment_method().Class){
				case 'Card':
					return this.validate_card();
				case 'Online':
					// Do things
					return this.validate_online();
				case 'Offline':
					return this.validate_offline();
				default:
					this.error_message = this.getError('MissingPaymentMethod');
					return;
			}
		},
		validate_online: function(){
			this.error_message = '';
			this.warning_message = '';
			this.$emit('success', true);
		},
		validate_offline: function(){
			this.error_message = '';
			this.warning_message = '';

			// If there are issuers, do more
			if(this.payment_method().Issuers !== undefined && this.payment_method().Issuers.length > 0){
				if(this.issuer() == ""){
					this.error_message = this.getError('MissingFinancialInstitution');
				}
			}

			// Caught an error? Stop!
			if(this.error_message.length > 0) return;

			this.$emit('success', true);
		},
		validate_card: function(){
			this.error_message = '';
			this.warning_message = '';

			var onlyNumbers = /^\d+$/;

			// Manually validate Credit Card Data as much as possible (Amex length = 15, others length = 16)
			if(this.cleaned_info.Number.trim().length != 16 && this.payment_method().Id != "AMEX") this.error_message = this.getError('InvalidCardNumber');
			else if(this.cleaned_info.Number.trim().length != 15 && this.payment_method().Id == "AMEX") this.error_message = this.getError('InvalidCardNumber');
			else if(!onlyNumbers.test(this.cleaned_info.Number)) this.error_message = this.getError('InvalidCardNumber');
			else if(!onlyNumbers.test(this.cleaned_info.VerificationCode)) this.error_message = this.getError('InvalidCardSecurityCode');
			// Validate cardholder name
			else if(this.cleaned_info.Name.trim().length < 5) this.error_message = this.getError('InvalidCardholderName');
			// Validate Credit cards (Amex CVV is 4 characters, others are 3)
			else if(this.payment_method().Id != "AMEX" && this.cleaned_info.VerificationCode.length != 3) this.error_message = this.getError('InvalidCardSecurityCode');
			else if(this.payment_method().Id == "AMEX" && this.cleaned_info.VerificationCode.length != 4) this.error_message = this.getError('InvalidCardSecurityCode');
			// Validate expiry
			else if(!onlyNumbers.test(this.cleaned_info.Expiry.Year)) this.error_message = this.getError('InvalidCardExpiry');
			else if(!onlyNumbers.test(this.cleaned_info.Expiry.Month)) this.error_message = this.getError('InvalidCardExpiry');

			// DO actual validation on card date
			var card_date = new Date(this.cleaned_info.Expiry.Year+"-"+this.cleaned_info.Expiry.Month+"-01");
			var temp_date = new Date();
			var current_date = new Date(temp_date.getFullYear()+"-"+(((temp_date.getMonth()*1)+1) < 10?"0"+((temp_date.getMonth()*1)+1):((temp_date.getMonth()*1)+1))+"-01");

			if(card_date <= current_date){
				this.error_message = this.getError('CardPostExpiry');
			}

			// Caught an error? Stop!
			if(this.error_message.length > 0) return;

			store.commit(SET_PAYMENT_DETAILS, this.collected_info);

			// Stash Card Data & present review step screen
			CheckoutService.stash({
				MerchantId: this.merchant().MerchantId,
				StashId: this.generate_stash_id(),
				data: {
					DeviceFingerprint: this.get_fingerprint(),
					card: this.cleaned_info
				}
			}).then( data => {
				//Handle Validation errors
				if(data.data.Warnings.length > 0){
					store.commit(CLEAR_STASH);
					this.error_message = this.getError(data.data.Warnings[0]);
					return;
				} else {
					store.commit(SET_STASH_DATA, data.data);
					this.$emit('success', true);
				}
			})
			.catch(error => {
				// Checkout Failure! Show error
				store.commit(CLEAR_STASH);
				if(error.response !== undefined && error.response.data !== undefined && error.response.data.length > 0){
					this.error_message = error.response.data;
					return;
				}
				this.error_message = this.getError('StashFailure');
			});
			return;
		},
		generate_stash_id: function(){
			// Generate an arbitrary string to use as stash identifier
			return md5(this.invoice().id+"-"+this.invoice().amount+"-"+this.rateOffer().Currency+"-"+Date.now());
		},
		...mapGetters(["payment_method", "financing_options", "payment_details", "invoice", "issuer", "rateOffer", "merchant", "get_fingerprint"])
	}
}
</script>
